import { Wrench, Truck, Snowflake, Package, Building2, ShoppingBag, ArrowUpRight, Calculator as CalcIcon, Phone, Send, Target } from "lucide-react";
import { SERVICES, COMPANY } from "@/lib/content";

const ICONS = {
  wrench: Wrench,
  truck: Truck,
  snowflake: Snowflake,
  package: Package,
  building: Building2,
  "shopping-bag": ShoppingBag,
  target: Target,
};

function ServiceCard({ s, idx }) {
  const Icon = ICONS[s.icon] || Wrench;

  const inner = (
    <>
      <div className="absolute inset-0 bg-gradient-to-br from-violet-500/0 via-transparent to-blue-500/0 group-hover:from-violet-500/5 group-hover:to-blue-500/5 transition-all duration-500" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/0 to-transparent group-hover:via-violet-400/60 transition-all duration-500" />

      <div className="relative">
        <div className="flex items-start justify-between mb-6">
          <div className="w-12 h-12 grid place-items-center rounded-sm border border-gray-200 bg-gray-50 group-hover:border-violet-500/50 group-hover:bg-violet-500/10 transition-all">
            <Icon className="w-5 h-5 text-violet-600" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-300 font-mono">
            0{idx + 1}
          </div>
        </div>

        <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-gray-900 mb-2">
          {s.title}
        </h3>
        <p className="text-sm text-gray-500 mb-4 min-h-[40px]">{s.short}</p>
        <p className="text-sm text-gray-400 leading-relaxed mb-6">{s.desc}</p>

        <div className="flex items-center justify-between pt-5 border-t border-gray-200">
          <div className="inline-flex items-center gap-2 text-sm font-bold text-violet-600 font-display tracking-wide uppercase">
            {s.cta === "calculator" && <CalcIcon className="w-4 h-4" />}
            {s.cta === "call" && <Phone className="w-4 h-4" />}
            {s.cta === "fleet" && <Send className="w-4 h-4" />}
            {s.cta === "external" && <ShoppingBag className="w-4 h-4" />}
            {s.ctaLabel}
          </div>
          <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-violet-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
        </div>
      </div>
    </>
  );

  const baseCls =
    "group relative bg-[#F0F1F5] p-7 md:p-9 hover:bg-white transition-all duration-300 cursor-pointer overflow-hidden block";

  if (s.cta === "external") {
    return (
      <a
        data-testid={`service-card-${s.id}`}
        href={s.link}
        target="_blank"
        rel="noopener noreferrer"
        className={baseCls}
      >
        {inner}
      </a>
    );
  }
  if (s.cta === "call") {
    return (
      <a
        data-testid={`service-card-${s.id}`}
        href={`tel:${COMPANY.phoneRaw}`}
        className={baseCls}
      >
        {inner}
      </a>
    );
  }
  if (s.cta === "fleet") {
    return (
      <a data-testid={`service-card-${s.id}`} href="#flotta" className={baseCls}>
        {inner}
      </a>
    );
  }
  // default: calculator
  return (
    <a data-testid={`service-card-${s.id}`} href="#arkalkulator" className={baseCls}>
      {inner}
    </a>
  );
}

export default function Services() {
  return (
    <section
      id="szolgaltatasok"
      data-testid="services-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-violet-600 font-bold mb-4">
              [ 01 ] · Szolgáltatások
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95]">
              Mindent megoldunk
              <br />
              <span className="text-gray-400">a négy kerekeden.</span>
            </h2>
          </div>
          <p className="text-gray-500 max-w-md text-base leading-relaxed">
            Mobil szerelőautónk profi felszereléssel érkezik. Defekt, gumicsere, szezonális csere
            vagy flotta szerződés — mindenre van megoldás.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100/10">
          {SERVICES.map((s, idx) => (
            <ServiceCard key={s.id} s={s} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
